import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  formatSupplyAgentRuleLabel,
  normalizeSupplyAgentRules,
} from '@/modules/supply/components/supplyAgentRuleUtils';
import { requestSupplyAgentOpen } from '@/modules/supply/components/supplyAgentDrawer';
import { supplyAgentService } from '@/services/supplyAgentService';

interface SupplySelectedSupplierRulesHintProps {
  supplierId: number;
  warehouseId: number;
}

const VISIBLE_CHIPS = 4;

export function SupplySelectedSupplierRulesHint({
  supplierId,
  warehouseId,
}: SupplySelectedSupplierRulesHintProps) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['supply-agent-supplier-rules', warehouseId, supplierId],
    queryFn: () => supplyAgentService.listRules(supplierId, warehouseId),
    enabled: warehouseId > 0 && supplierId > 0,
    staleTime: 15_000,
  });

  const rules = normalizeSupplyAgentRules(query.data?.rules).filter((rule) => rule.is_active !== false);
  const extra = Math.max(0, rules.length - VISIBLE_CHIPS);

  return (
    <div className="mt-3 border-t border-blue-200/80 pt-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {query.isLoading ? (
            <p className="text-xs text-blue-800/80">{t('supply.selectedSupplier.rulesLoading')}</p>
          ) : rules.length === 0 ? (
            <p className="text-xs text-blue-800/80">{t('supply.selectedSupplier.rulesNone')}</p>
          ) : (
            <>
              <p className="text-xs font-medium text-blue-900">
                {t('supply.selectedSupplier.rulesCount', { count: rules.length })}
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1">
                {rules.slice(0, VISIBLE_CHIPS).map((rule, index) => (
                  <li
                    key={String(rule.id ?? `${rule.rule_type}-${index}`)}
                    className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[11px] text-blue-900"
                    title={formatSupplyAgentRuleLabel(rule)}
                  >
                    {formatSupplyAgentRuleLabel(rule)}
                  </li>
                ))}
                {extra > 0 ? (
                  <li className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[11px] text-blue-800">
                    {t('supply.selectedSupplier.rulesMore', { count: extra })}
                  </li>
                ) : null}
              </ul>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => requestSupplyAgentOpen()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-100"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t('supply.selectedSupplier.openAgent')}
        </button>
      </div>
    </div>
  );
}
